import { QueryClient } from "@tanstack/react-query";

/**
 * How long a fetched admin list stays authoritative before a tab refocus is
 * allowed to re-read it.
 *
 * This is the guard that makes focus refetching affordable. Several admin
 * lists are unbounded reads — pharmacy-admin's Consultations page pulls the
 * tenant's entire `consultations` collection with no `limit()` — so without a
 * stale window every alt-tab would re-download the lot. Thirty seconds is long
 * enough to absorb ordinary tab-flicking and short enough that coming back to
 * a panel after a meeting shows current data.
 *
 * Mutations are unaffected: the panels invalidate after every write, and
 * invalidation forces a fetch regardless of this value.
 */
export const ADMIN_STALE_TIME = 30_000;

/**
 * Poll cadence for the dashboard counters, which are aggregates rather than
 * lists and so cannot rely on a refocus to look current.
 *
 * Only the three Dashboard pages should use this. A list page gets its
 * freshness from the focus/reconnect refetch configured below — adding an
 * interval on top would re-read the collection while someone is reading it.
 */
export const ADMIN_REFETCH_INTERVAL = 60_000;

/**
 * The query client every admin panel runs on.
 *
 * Replaces the three copy-pasted `new QueryClient({...})` literals that used to
 * live in each panel's `App.tsx`, all of which set `refetchOnWindowFocus:
 * false` — meaning a panel left open on a second monitor never refreshed at
 * all. An admin acting on a booking status or an invite that someone else
 * changed hours ago is the failure this exists to prevent.
 *
 * Only the options that differ from a decision worth recording are set.
 * `refetchOnReconnect` (already `true`), `refetchIntervalInBackground` (already
 * off, so the dashboards do not poll hidden tabs) and the mutation retry
 * (already 0) are left at their library defaults rather than restated here.
 */
export function createAdminQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: 1,
        staleTime: ADMIN_STALE_TIME,
        // Matches the library default. Stated explicitly because switching it
        // off is exactly the regression this module exists to prevent.
        refetchOnWindowFocus: true,
      },
    },
  });
}
